'use client';
import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const MY_GRNS = [
  { ref: 'GRN-2026-0847', vendor: 'Tata Steel Ltd', status: 'PENDING_APPROVAL', created: '2h ago', value: '€ 38,400' },
  { ref: 'GRN-2026-0843', vendor: 'Siemens India', status: 'APPROVED', created: '1d ago', value: '€ 12,750' },
  { ref: 'GRN-2026-0839', vendor: 'Bosch Rexroth', status: 'DRAFT', created: '2d ago', value: '€ 5,200' },
];

const MY_GIS = [
  { ref: 'GI-2026-0319', workOrder: 'WO-2026-0041', status: 'PENDING_APPROVAL', items: 4, created: '6h ago' },
  { ref: 'GI-2026-0315', workOrder: 'WO-2026-0038', status: 'APPROVED_PENDING_ISSUE', items: 7, created: '1d ago' },
  { ref: 'GI-2026-0311', workOrder: 'WO-2026-0035', status: 'ISSUED', items: 3, created: '3d ago' },
];

const PENDING_ISSUE = [
  { ref: 'GI-2026-0315', items: 7, recipient: 'Kiran Bhat', workOrder: 'WO-2026-0038', site: 'Warehouse B' },
];

const STATUS_PILL: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-emerald-100 text-emerald-700',
  APPROVED_PENDING_ISSUE: 'bg-blue-100 text-blue-700',
  ISSUED: 'bg-teal/10 text-teal',
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending',
  APPROVED: 'Approved',
  APPROVED_PENDING_ISSUE: 'Ready to Issue',
  ISSUED: 'Issued',
};

export default function StoreOperatorDashboard() {
  return (
    <div className="space-y-5">
      {/* Role header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-orange-600 to-orange-800 rounded-2xl text-white">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
          <Icon name="UserIcon" size={22} />
        </div>
        <div>
          <p className="font-700 text-base">My Tasks</p>
          <p className="text-[12px] text-white/65">Store Operator · Rahul Kumar · Site Beta</p>
        </div>
        <div className="ml-auto flex gap-2">
          <Link href="/grn-management" className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-[12px] font-600 transition-colors">
            <Icon name="PlusIcon" size={13} /> New GRN
          </Link>
          <Link href="/goods-issuance-management" className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-accent-foreground hover:bg-amber-400 rounded-lg text-[12px] font-600 transition-colors">
            <Icon name="TruckIcon" size={13} /> New Issuance
          </Link>
        </div>
      </div>

      {/* Task summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'My GRNs', value: '3', sub: '1 pending approval', icon: 'DocumentTextIcon', color: 'bg-secondary/5 border-secondary/20', iconColor: 'bg-secondary/10 text-secondary' },
          { label: 'My Issuances', value: '3', sub: '1 ready to issue', icon: 'TruckIcon', color: 'bg-orange-50 border-orange-200', iconColor: 'bg-orange-100 text-orange-600' },
          { label: 'Pending Issue', value: '1', sub: 'Physical handover due', icon: 'ClockIcon', color: 'bg-amber-50 border-amber-200', iconColor: 'bg-amber-100 text-amber-600' },
          { label: 'Stock Counts', value: '2', sub: 'Assigned to me', icon: 'ClipboardDocumentCheckIcon', color: 'bg-teal/5 border-teal/20', iconColor: 'bg-teal/10 text-teal' },
        ].map((k) => (
          <div key={k.label} className={`rounded-xl border p-4 shadow-card ${k.color}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${k.iconColor}`}>
              <Icon name={k.icon as Parameters<typeof Icon>[0]['name']} size={18} />
            </div>
            <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground">{k.label}</p>
            <p className="text-3xl font-700 text-foreground tabular-nums mt-0.5">{k.value}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{k.sub}</p>
          </div>
        ))}
      </div>

      {/* Pending physical issue — action card */}
      {PENDING_ISSUE.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <Icon name="ExclamationTriangleIcon" size={20} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-700 text-amber-900">Action Required: Physical Issuance Pending</p>
              <p className="text-[12px] text-amber-700 mt-0.5">
                {PENDING_ISSUE[0].ref} — {PENDING_ISSUE[0].items} items approved for {PENDING_ISSUE[0].recipient} at {PENDING_ISSUE[0].site}
              </p>
              <p className="text-[11px] text-amber-600 mt-1">Work Order: {PENDING_ISSUE[0].workOrder}</p>
            </div>
            <Link href="/goods-issuance-management" className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white rounded-lg text-[12px] font-600 hover:bg-amber-700 transition-colors shrink-0">
              <Icon name="CheckIcon" size={13} /> Complete Issue
            </Link>
          </div>
        </div>
      )}

      {/* My GRNs + My GIs */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="DocumentTextIcon" size={15} className="text-secondary" />
              <h3 className="text-sm font-600 text-foreground">My GRNs</h3>
            </div>
            <Link href="/grn-management" className="text-[11px] text-secondary hover:underline">View all</Link>
          </div>
          <div className="divide-y divide-border">
            {MY_GRNS.map((g) => (
              <div key={g.ref} className="flex items-center gap-3 px-5 py-3.5 hover:bg-muted/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-600 font-mono-data text-foreground">{g.ref}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-600 ${STATUS_PILL[g.status]}`}>{STATUS_LABEL[g.status]}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{g.vendor} · {g.created}</p>
                </div>
                <p className="text-[12px] font-700 text-foreground tabular-nums shrink-0">{g.value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="TruckIcon" size={15} className="text-orange-500" />
              <h3 className="text-sm font-600 text-foreground">My Issuances</h3>
            </div>
            <Link href="/goods-issuance-management" className="text-[11px] text-secondary hover:underline">View all</Link>
          </div>
          <div className="divide-y divide-border">
            {MY_GIS.map((g) => (
              <div key={g.ref} className="flex items-center gap-3 px-5 py-3.5 hover:bg-muted/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-600 font-mono-data text-foreground">{g.ref}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-600 ${STATUS_PILL[g.status]}`}>{STATUS_LABEL[g.status]}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{g.workOrder} · {g.items} items · {g.created}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick scan widget */}
      <div className="bg-card border border-border rounded-2xl shadow-card p-5">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="QrCodeIcon" size={18} className="text-secondary" />
          <h3 className="text-sm font-600 text-foreground">Quick Barcode Lookup</h3>
        </div>
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Enter item code or scan barcode…"
            className="flex-1 px-4 py-2.5 border border-border rounded-xl text-[13px] bg-muted/30 focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary transition-all"
          />
          <button className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-white rounded-xl text-[13px] font-600 hover:bg-secondary/90 transition-colors">
            <Icon name="MagnifyingGlassIcon" size={15} /> Lookup
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Scan barcode to instantly view stock level, location, and recent movements</p>
      </div>
    </div>
  );
}
